/**
 * Safari bug guard: visualViewport.height reported as 0.
 *
 * Seen on the iPhone Duo's inner screen (Safari, iOS 26): intermittently
 * `visualViewport.height === 0` while `innerHeight` is correct (tap debugger
 * HUD showed `inner 867x669`, `vv 867x0`). Floating UI, which positions every
 * Radix popper (dropdowns, popovers, selects, tooltips), takes its viewport
 * rect from visualViewport.width/height. With a 0px-tall viewport, every
 * placement overflows, so `flip` picks the side that overflows least, which
 * for a header trigger is *above* it, off-screen. Because Radix dropdowns are
 * modal, the next tap only dismisses that invisible menu, which made the app
 * feel like it was ignoring taps.
 *
 * Only a zero height is patched. Any real value, including the shrunken
 * keyboard-open height, passes through untouched. Delete this file and its
 * call in main.tsx once WebKit stops reporting 0.
 */
let rawHeight: (() => number) | null = null;

/** The height Safari actually reports (for the tap debugger HUD). */
export function rawVisualViewportHeight(): number | null {
  return rawHeight && window.visualViewport ? rawHeight.call(window.visualViewport) : null;
}

export function guardVisualViewportHeight() {
  if (typeof window === 'undefined' || !('VisualViewport' in window)) return;
  const proto = window.VisualViewport.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'height');
  const nativeGet = desc?.get;
  if (!desc || !nativeGet) return;
  rawHeight = nativeGet as () => number;
  Object.defineProperty(proto, 'height', {
    ...desc,
    get(this: VisualViewport) {
      const h = nativeGet.call(this) as number;
      if (h > 0) return h;
      return document.documentElement.clientHeight || window.innerHeight;
    },
  });
}
